import type { ReactNode } from "react";
import { BOSH_USLUB, ulushFoiz } from "./scale";

export interface LollipopRow {
	key: string;
	label: ReactNode;
	total: number;
	/** `total` ning ajratilgan qismi (masalan ruxsatsiz chiqishlar). */
	flagged: number;
}

export interface LollipopProps {
	rows: LollipopRow[];
	/** Berilmasa — eng katta `total`. */
	max?: number;
	totalColor: string;
	flaggedColor: string;
	/** `title` dagi nomlar (reja imzosidan kengaytirilgan). */
	totalLabel?: string;
	flaggedLabel?: string;
	/** Qator yo'q bo'lganda matn (reja imzosidan kengaytirilgan). */
	empty?: string;
}

function matnYorliq(label: ReactNode): string | undefined {
	return typeof label === "string" || typeof label === "number" ? String(label) : undefined;
}

function izoh(qator: LollipopRow, totalLabel?: string, flaggedLabel?: string): string {
	const jami = totalLabel ? `${totalLabel} ${qator.total}` : String(qator.total);
	const ajratilgan = flaggedLabel ? `${flaggedLabel} ${qator.flagged}` : String(qator.flagged);
	const nom = matnYorliq(qator.label);
	const qiymat = `${jami}, ${ajratilgan}`;

	return nom === undefined ? qiymat : `${nom}: ${qiymat}`;
}

/**
 * Lollipop ro'yxati — poya `total` gacha, ustida `flagged` nuqtasi
 * (bir shkalada).
 *
 * ⚠️ **Ikkala son o'ng chetda MATN sifatida ham turadi** — rang va
 * uzunlik yagona kanal bo'lmasin (relief qoidasi, spec §2.1).
 *
 * ⚠️ `flagged === 0` da nuqta `hidden` — «0» nuqtasi poya boshida
 * «bor, lekin kichik» bilan adashtirilardi. Raqam baribir chiqadi.
 *
 * ⚠️ `data-lollipop-*` — TEST KONTRAKTI.
 */
export function Lollipop({
	rows,
	max,
	totalColor,
	flaggedColor,
	totalLabel,
	flaggedLabel,
	empty,
}: LollipopProps) {
	if (rows.length === 0) {
		return (
			<div className="py-4 text-center text-xs" style={BOSH_USLUB}>
				{empty}
			</div>
		);
	}

	const shkala = max ?? Math.max(0, ...rows.map((q) => q.total));

	return (
		<div className="flex flex-col gap-2">
			{rows.map((qator) => {
				const nom = matnYorliq(qator.label);

				return (
					<div
						key={qator.key}
						data-lollipop-row
						title={izoh(qator, totalLabel, flaggedLabel)}
						className="flex items-center gap-3"
					>
						<div className="w-36 shrink-0 truncate text-xs" title={nom}>
							{qator.label}
						</div>

						<div className="relative h-2.5 flex-1">
							<div
								data-lollipop-stem
								className="absolute top-1/2 left-0 h-0.5 -translate-y-1/2"
								style={{ width: `${ulushFoiz(qator.total, shkala)}%`, backgroundColor: totalColor }}
							>
								<span
									aria-hidden="true"
									className="absolute top-1/2 right-0 h-2 w-2 translate-x-1/2 -translate-y-1/2 rounded-full"
									style={{ backgroundColor: totalColor }}
								/>
							</div>
							<span
								data-lollipop-flagged-dot
								aria-hidden="true"
								hidden={qator.flagged <= 0}
								className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
								style={{
									left: `${ulushFoiz(qator.flagged, shkala)}%`,
									backgroundColor: flaggedColor,
									boxShadow: "0 0 0 2px var(--chart-surface)",
								}}
							/>
						</div>

						<div className="flex w-16 shrink-0 justify-end gap-1.5 text-xs tabular-nums">
							<span data-lollipop-total style={{ color: "var(--chart-ink)" }}>
								{qator.total}
							</span>
							<span data-lollipop-flagged className="inline-flex items-center gap-1">
								<span
									aria-hidden="true"
									className="inline-block h-1.5 w-1.5 rounded-full"
									style={{ backgroundColor: flaggedColor }}
								/>
								{qator.flagged}
							</span>
						</div>
					</div>
				);
			})}
		</div>
	);
}
